type Language = 'ko' | 'en' | 'ja';
import { Typography } from '@common/styles';
import {
  formatKoreanNumber,
  formatPaymentMethodLabel,
  formatPaymentMethodLabelKey,
  GetMaximumBenefit_payment_event_list_for_display_item_list as PaymentEvent,
  PaymentMethod,
  PaymentNotificationItemList,
  PaymentNotificationType,
  VendorEventType,
} from '@domains/pdp-common';

import { BottomSheetInfo } from '../services/usePaymentService';
import { findMaxDiscountAmount } from './findMaxDiscountAmount';
import { formatBoldText, formatDateRange } from './format';

type CombinePaymentList = PaymentEvent | PaymentNotificationItemList;

function isPaymentMethodType(item: CombinePaymentList): item is PaymentEvent {
  return 'payment_method_type' in item;
}

export function isPaymentEvent(item: CombinePaymentList): item is PaymentEvent {
  return 'event_type' in item;
}

export function isPaymentNotification(item: CombinePaymentList): item is PaymentNotificationItemList {
  return 'notification_type' in item;
}

function getPaymentEventBottomSheetInfo(
  language: Language,
  paymentList: CombinePaymentList,
  maxDiscountAmount: number,
  replaceContent?: string,
) {
  const has_payment_method_type = isPaymentMethodType(paymentList);
  const benefit_name = formatPaymentMethodLabel(
    language,
    paymentList.financial_company_code as string,
    paymentList.payment_method,
    has_payment_method_type ? paymentList.payment_method_type : null,
  );
  const key = getPaymentEventBottomSheetMapKey(language, paymentList);
  const title = `${benefit_name} 최대 ${formatKoreanNumber(maxDiscountAmount)} 할인`;
  const content = getContent(language, paymentList, replaceContent);
  return { key, title, content };
}

function getContent(language: Language, item: CombinePaymentList, replaceContent?: string): string[] {
  if (isPaymentEvent(item)) {
    const { payment_event_detail_list, date_started, date_ended } = item;
    const content: string[] = replaceContent
      ? [formatBoldText(replaceContent)]
      : payment_event_detail_list.map(
          ({ min_amount, discount_amount }) =>
            `${formatKoreanNumber(min_amount)} 이상 결제 시 ${formatKoreanNumber(discount_amount)} 즉시할인`,
        );
    content.push(`기간: ${formatDateRange(date_started, date_ended)}`);
    return content;
  }

  if (isPaymentNotification(item)) {
    const { min_amount, discount_amount, payment_notification_content_list } = item;
    return [
      ...payment_notification_content_list
        .filter((c) => c.language === language)
        .map(({ content }) => formatBoldText(content, Typography.BODY_13_MEDIUM)),
      `${formatKoreanNumber(min_amount ?? 0)} 이상 결제 시 ${formatKoreanNumber(discount_amount ?? 0)} 즉시할인`,
    ];
  }

  return [];
}

export function getPaymentEventBottomSheetMapKey(
  language: Language,
  paymentList: CombinePaymentList,
  default_benefit_name?: string,
) {
  const has_payment_method_type = isPaymentMethodType(paymentList);
  const benefit_name =
    default_benefit_name ??
    formatPaymentMethodLabelKey(
      language,
      paymentList.financial_company_code as string,
      paymentList.payment_method,
      has_payment_method_type ? paymentList.payment_method_type : null,
    );

  const key = [benefit_name];

  if (isPaymentEvent(paymentList)) {
    key.push(paymentList.event_type);
  }

  if (isPaymentNotification(paymentList)) {
    key.push(paymentList.payment_method);
  }
  return key.join('_');
}

export function initialPaymentBottomSheetInfo(
  language: Language,
  paymentEventList: PaymentEvent[],
  paymentEventNotificationList: PaymentNotificationItemList[],
  paymentGuideNotificationList: PaymentNotificationItemList[],
) {
  const initialEvent = new Map() as Map<string, BottomSheetInfo>;
  const combineArr = ([] as CombinePaymentList[]).concat(paymentEventNotificationList).concat(paymentEventList);

  const kakaoPayGuideContent = paymentGuideNotificationList
    .flatMap((o) => o.payment_notification_content_list.map((o2) => o2.content))
    .join('');

  return combineArr.reduce((acc, cur) => {
    if (isPaymentEvent(cur) && cur.payment_event_detail_list.length > 0) {
      const maxDiscountAmount = findMaxDiscountAmount(cur.payment_event_detail_list);
      const addGuideContent =
        cur.payment_method === PaymentMethod.KAKAOPAY && cur.event_type === VendorEventType.PG_COUPON;
      const { key, title, content } = getPaymentEventBottomSheetInfo(
        language,
        cur,
        maxDiscountAmount,
        addGuideContent ? kakaoPayGuideContent : '',
      );
      acc.set(key, { title, content });
    }

    if (isPaymentNotification(cur)) {
      const { key, title, content } = getPaymentEventBottomSheetInfo(language, cur, cur.min_amount ?? 0);
      acc.set(key, { title, content });
    }
    return acc;
  }, initialEvent);
}

export function initialPaymentNotification(list: PaymentNotificationItemList[]) {
  const initialNotification = {
    paymentGuideNotificationList: [] as PaymentNotificationItemList[],
    paymentEventNotificationList: [] as PaymentNotificationItemList[],
    paymentEventNotificationHigherDiscount: 0,
  };

  return list.reduce((acc, cur) => {
    if (cur.notification_type === PaymentNotificationType.GUIDE && cur.payment_method === PaymentMethod.KAKAOPAY) {
      acc.paymentGuideNotificationList.push(cur);
    }
    if (cur.notification_type === PaymentNotificationType.EVENT && cur.discount_amount && cur.discount_amount > 0) {
      acc.paymentEventNotificationList.push(cur);
      acc.paymentEventNotificationHigherDiscount = Math.max(
        acc.paymentEventNotificationHigherDiscount,
        cur.discount_amount,
      );
    }
    return acc;
  }, initialNotification);
}

export function initialPaymentEventListForDisplay(list: PaymentEvent[]) {
  return list.filter((o) => Boolean(o.payment_event_detail_list.length));
}

export function initialPaymentEvent(list: PaymentEvent[]) {
  const initialEvent = {
    paymentEventListHigherDiscount: 0,
  };

  return list.reduce((acc, cur) => {
    if (cur.payment_event_detail_list.length > 0) {
      const maxDiscountAmount = findMaxDiscountAmount(cur.payment_event_detail_list);
      acc.paymentEventListHigherDiscount = Math.max(acc.paymentEventListHigherDiscount, maxDiscountAmount);
    }

    return acc;
  }, initialEvent);
}

export function getEventNameList(language: Language, map: Map<string, CombinePaymentList>) {
  const keys = Array.from(map.keys());
  const paymentLabels: string[] = [];

  keys.forEach((key) => {
    const item = map.get(key);
    if (item) {
      const has_payment_method_type = isPaymentMethodType(item);
      paymentLabels.push(
        formatPaymentMethodLabel(
          language,
          item.financial_company_code as string,
          item.payment_method,
          has_payment_method_type ? item.payment_method_type : '',
        ),
      );
    }
  });
  return paymentLabels;
}

export function getUniqEventMap(
  payment_event_list_for_display: PaymentEvent[],
  payment_notification_list: PaymentNotificationItemList[],
) {
  const arr: Array<CombinePaymentList> = [];
  return arr
    .concat(payment_notification_list)
    .concat(payment_event_list_for_display)
    .reduce((acc, cur) => {
      const event_type = isPaymentEvent(cur) ? `-${cur.event_type}` : '';
      const payment_method_type = isPaymentEvent(cur) ? cur.payment_method_type : '';
      acc.set(`${cur.financial_company_code}_${event_type}_${cur.payment_method}_${payment_method_type}`, cur);
      return acc;
    }, new Map());
}
